import React from 'react';
import get from 'lodash/get';
import { FormattedMessage } from 'react-intl';
import SelectionCell from 'shared/ui/Table/cells/SelectionCell';
import SelectionHeader from 'shared/ui/Table/cells/SelectionHeader';
import formatNumber from 'Common/Functions/FormatNumber';
import {
  Ip,
  Icon,
  Hostnames,
  CenteredIcon,
  CenteredNumericRow
} from 'Screens/Contextualization/Host/components/Table/styled';
import { Column } from 'Screens/Contextualization/HostsGeneral/components/Table/styled';

const getHostRowId = (row) => get(row, '_id', get(row, 'id', null));
const oss = ['windows', 'cisco', 'router', 'osx', 'apple', 'mac', 'linux', 'unix'];
const getHostOsType = (os) => (os ? oss.find((item) => (os.toLowerCase().includes(item) ? item : '')) : '');
const isRowSelected = (selectedRowIds, rowId) => rowId !== null &&
  (Array.isArray(selectedRowIds) ? selectedRowIds.map(String) : []).includes(String(rowId));

const renderNumeric = (rowInfo, attrib) => {
  const value = get(rowInfo, `original.${attrib}`, '');
  return <CenteredNumericRow>{formatNumber(value)}</CenteredNumericRow>;
};

// Legacy tolerated a missing `fields` (`fields ?? {}`).
const field = (fields, key) => (fields ?? {})[key];
const minWidthProps = (fieldKey, className) => (rowInfo, { fields }) => ({
  className: `${className ? `${className} ` : ''}min-wd-${field(fields, fieldKey)?.defaultMin}`
});

/**
 * "Assets" tab of the Explore group view (Manage).
 * ctx: `{ fields, selectedRowIds, allSelected, onToggleRow, onToggleAll, onOpenHostDetail }`
 */
const exploreHostsSchema = {
  id: 'exploreGroupHosts',
  loading: { mockRows: 13 },
  ordering: { keepFirstColumn: false },
  columns: [
    {
      header: (headerInfo, { allSelected, onToggleAll }) => <SelectionHeader checked={ !!allSelected } onChange={ onToggleAll } />,
      accessor: 'selected',
      id: 'selected',
      maxWidth: 38,
      minWidth: 38,
      resizable: false,
      sortable: false,
      visible: true,
      order: -1,
      cell: ({ original }, { selectedRowIds, onToggleRow }) => {
        const isSelected = isRowSelected(selectedRowIds, getHostRowId(original));
        return <SelectionCell checked={ isSelected } onChange={ () => onToggleRow(original) } />;
      }
    },
    {
      header: () => <FormattedMessage id="host.table.columns.ip" />,
      id: 'ip',
      accessor: 'ip',
      visible: ({ fields }) => field(fields, 'asset')?.visible,
      sortable: false,
      resizable: true,
      minWidth: ({ fields }) => field(fields, 'asset')?.minWidth,
      maxWidth: ({ fields }) => field(fields, 'asset')?.maxWidth,
      order: ({ fields }) => field(fields, 'asset')?.order,
      cell: ({ original }, { onOpenHostDetail }) => (
        <Ip onClick={ () => onOpenHostDetail(original) }>{get(original, 'ip', '')}</Ip>
      ),
      cellProps: minWidthProps('asset', 'asset')
    },
    {
      header: () => <div children="RISK SCORE" />,
      id: 'risk',
      accessor: 'risk',
      visible: ({ fields }) => field(fields, 'risk')?.visible,
      sortable: false,
      resizable: true,
      maxWidth: ({ fields }) => field(fields, 'risk')?.maxWidth,
      minWidth: ({ fields }) => field(fields, 'risk')?.minWidth,
      order: ({ fields }) => field(fields, 'risk')?.order,
      cell: (rowInfo) => renderNumeric(rowInfo, 'risk'),
      cellProps: minWidthProps('risk')
    },
    {
      header: () => <FormattedMessage id="host.table.columns.os" />,
      id: 'os',
      accessor: 'os',
      visible: ({ fields }) => field(fields, 'os')?.visible,
      sortable: false,
      resizable: true,
      minWidth: ({ fields }) => field(fields, 'os')?.minWidth,
      maxWidth: ({ fields }) => field(fields, 'os')?.maxWidth,
      order: ({ fields }) => field(fields, 'os')?.order,
      cell: (rowInfo) => {
        const os = getHostOsType(get(rowInfo, 'original.os', ''));
        return (
          <CenteredIcon>
            <Icon alt={ `${os}` } type={ os } />
          </CenteredIcon>
        );
      },
      cellProps: minWidthProps('os', 'os')
    },
    {
      header: () => <FormattedMessage id="host.table.columns.services" />,
      id: 'services__name',
      visible: ({ fields }) => field(fields, 'services')?.visible,
      sortable: false,
      resizable: true,
      maxWidth: 150,
      minWidth: ({ fields }) => field(fields, 'services')?.minWidth,
      order: ({ fields }) => field(fields, 'services')?.order,
      cell: (rowInfo) => {
        const summaries = get(rowInfo, 'original.service_summaries', []);
        return <Column>{Array.isArray(summaries) ? summaries.join(', ') : summaries}</Column>;
      },
      cellProps: minWidthProps('services')
    },
    {
      header: () => <FormattedMessage id="host.table.columns.hostnames" />,
      id: 'hostnames',
      visible: ({ fields }) => field(fields, 'hostnames')?.visible,
      sortable: false,
      resizable: true,
      minWidth: 200,
      maxWidth: 300,
      order: ({ fields }) => field(fields, 'hostnames')?.order,
      cell: (rowInfo) => {
        const hostnames = get(rowInfo, 'original.hostnames', []);
        return <Hostnames>{Array.isArray(hostnames) ? hostnames.join(', ') : hostnames}</Hostnames>;
      },
      cellProps: minWidthProps('hostnames')
    },
    {
      header: () => <FormattedMessage id="host.table.columns.open_services" />,
      id: 'open_service_count',
      visible: ({ fields }) => field(fields, 'open_services')?.visible,
      sortable: false,
      resizable: true,
      maxWidth: ({ fields }) => field(fields, 'open_services')?.maxWidth,
      minWidth: ({ fields }) => field(fields, 'open_services')?.minWidth,
      order: ({ fields }) => field(fields, 'open_services')?.order,
      // Legacy: renders `services`, not `open_services`.
      cell: (rowInfo) => renderNumeric(rowInfo, 'services'),
      cellProps: minWidthProps('open_services')
    },
    {
      header: () => <FormattedMessage id="host.table.columns.vulns" />,
      id: 'vulnerability_count',
      accessor: 'vulns',
      visible: ({ fields }) => field(fields, 'vulns')?.visible,
      sortable: false,
      resizable: true,
      maxWidth: ({ fields }) => field(fields, 'vulns')?.maxWidth,
      minWidth: ({ fields }) => field(fields, 'vulns')?.minWidth,
      order: ({ fields }) => field(fields, 'vulns')?.order,
      cell: (rowInfo) => renderNumeric(rowInfo, 'vulns'),
      cellProps: minWidthProps('vulns')
    }
  ]
};

export default exploreHostsSchema;
